import { useMemo } from 'react';

import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/data-table/data-table';
import {
  DefaultTableErrorState,
  DefaultTableLoadingState,
} from '@/components/data-table/data-table-states';
import { Button } from '@/components/ui/button';
import { useColumnSizing } from '@/hooks/use-column-sizing';
import { TableIds } from '@/lib/constants/table-ids';
import { useApiError } from '@/modules/common/hooks/use-api-error';

import { getPaymentDetailsColumns } from '../../constants/payment-details-table-columns';
import { PaymentDetailsEntry } from '../../types/payment-details';

interface PaymentDetailsTableProps {
  entries: PaymentDetailsEntry[];
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  onEdit: (entry: PaymentDetailsEntry) => void;
  onDelete: (entry: PaymentDetailsEntry) => void;
  onAdd: () => void;
}

export const PaymentDetailsTable = ({
  entries,
  isLoading,
  error,
  onRetry,
  onEdit,
  onDelete,
  onAdd,
}: PaymentDetailsTableProps) => {
  const { t } = useTranslation(['authenticated.settings', 'common']);
  const { getErrorMessage } = useApiError();

  const {
    columnSizing,
    columnSizingInfo,
    onColumnSizingChange,
    onColumnSizingInfoChange,
    saveColumnSizing,
  } = useColumnSizing(TableIds.PAYMENT_DETAILS);

  const columns = useMemo(
    () => getPaymentDetailsColumns({ t, onEdit, onDelete }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const table = useReactTable({
    data: entries,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    state: { columnSizing, columnSizingInfo },
    onColumnSizingChange,
    onColumnSizingInfoChange,
  });

  const colSpan = table.getAllColumns().length;

  const lastRowChildren = (
    <>
      {isLoading && <DefaultTableLoadingState colSpan={colSpan} />}
      {error && !isLoading && (
        <DefaultTableErrorState
          colSpan={colSpan}
          error={getErrorMessage(error)}
          retryLoad={onRetry}
        />
      )}
      {!isLoading && !error && entries.length === 0 && (
        <tr>
          <td colSpan={colSpan} className="h-16 px-4">
            <div className="text-sm text-muted-foreground">
              {t('authenticated.settings:pages.finance.paymentDetails.table.empty')}
            </div>
          </td>
        </tr>
      )}
      {!isLoading && !error && (
        <tr className="hover:bg-transparent">
          <td colSpan={colSpan} className="h-16 px-4">
            <div className="flex justify-start">
              <Button type="button" variant="ghost" size="sm" className="gap-2" onClick={onAdd}>
                <CirclePlus className="h-4 w-4" />
                {t('authenticated.settings:pages.finance.paymentDetails.card.addButton')}
              </Button>
            </div>
          </td>
        </tr>
      )}
    </>
  );

  return (
    <DataTable
      table={table}
      lastRowChildren={lastRowChildren}
      tableId={TableIds.PAYMENT_DETAILS}
      onResizeEnd={saveColumnSizing}
    />
  );
};
